import { Alerta, Esqueleto, EstadoVazio, cn } from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { clientesApi } from '../../api/leads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { EtiquetaStatus } from '../../componentes/etiquetas-dominio'
import { formatarData } from '../../utilitarios/formatar'

/**
 * Aba "Histórico do Cliente" — todos os atendimentos anteriores da mesma pessoa,
 * inclusive de outras equipes e unidades (RN-17).
 *
 * O nível de detalhe é do servidor: `RESUMO` para quem não tem
 * `CLIENTE_HISTORICO_INTEGRAL`, `INTEGRAL` para Supervisor+. A tela mostra o que
 * recebeu e **avisa qual nível** está vendo — sem isso, o atendente acharia que
 * atendimentos antigos não tiveram conversa registrada.
 */
export function HistoricoCliente({
  clienteUuid,
  leadAtualUuid,
}: {
  clienteUuid: string
  leadAtualUuid: string
}) {
  const consulta = useQuery({
    queryKey: chaves.clientes.historico(clienteUuid),
    queryFn: ({ signal }) => clientesApi.historico(clienteUuid, signal),
    staleTime: 60_000,
  })

  if (consulta.isPending) {
    return (
      <div className="space-y-3 p-4">
        {Array.from({ length: 3 }, (_, indice) => (
          <Esqueleto key={indice} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }

  const historico = consulta.data
  const anteriores = historico.leads.filter((lead) => lead.uuid !== leadAtualUuid)

  if (anteriores.length === 0) {
    return (
      <EstadoVazio
        titulo="Primeiro atendimento deste cliente"
        descricao="Não há atendimentos anteriores registrados para esta pessoa."
      />
    )
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {historico.nivel === 'RESUMO' && (
        <Alerta tom="info">
          Você vê o resumo dos atendimentos anteriores (datas, equipe, desfecho). O conteúdo das
          conversas de outras equipes fica restrito a supervisão e gestão.
        </Alerta>
      )}

      <ul className="flex flex-col gap-3">
        {anteriores.map((lead) => (
          <li
            key={lead.uuid}
            className={cn(
              'rounded-lg border p-3',
              lead.ehAtual ? 'border-marca-300 bg-marca-50' : 'border-borda bg-superficie',
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <EtiquetaStatus status={lead.status} />
                <span className="text-xs tabular-nums text-conteudo-fraco">
                  {formatarData(lead.chegadaEm)}
                  {lead.finalizadoEm ? ` → ${formatarData(lead.finalizadoEm)}` : ''}
                </span>
              </div>
              <Link
                to={`/leads/${lead.uuid}`}
                className="text-xs font-medium text-marca-600 hover:underline"
              >
                Abrir atendimento
              </Link>
            </div>

            <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
              <Par rotulo="Unidade" valor={lead.unidade.titulo} />
              <Par rotulo="Equipe" valor={lead.equipe?.titulo ?? '—'} />
              <Par rotulo="Responsável" valor={lead.responsavel?.nome ?? 'Sem responsável'} />
              <Par rotulo="Ponto de conversão" valor={lead.pontoConversao?.titulo ?? '—'} />
              {lead.motivoVendaNaoRealizada ? (
                <Par rotulo="Motivo da perda" valor={lead.motivoVendaNaoRealizada.titulo} />
              ) : null}
              <Par rotulo="Interações" valor={String(lead.totalInteracoes)} />
            </dl>

            {lead.interacoes && lead.interacoes.length > 0 ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-semibold text-conteudo-suave hover:text-conteudo">
                  Ver interações ({lead.interacoes.length} de {lead.totalInteracoes})
                </summary>
                <ul className="mt-2 flex flex-col gap-2 border-l-2 border-borda pl-3">
                  {lead.interacoes.map((interacao) => (
                    <li key={interacao.id} className="text-xs">
                      <p className="text-conteudo-fraco">
                        {formatarData(interacao.ocorreuEm)} ·{' '}
                        {interacao.autor?.nome ?? 'sistema'}
                      </p>
                      {interacao.mensagem ? (
                        <p className="mt-0.5 whitespace-pre-wrap text-conteudo">
                          {interacao.mensagem}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {lead.totalInteracoes > lead.interacoes.length ? (
                  <p className="mt-1.5 text-[0.6875rem] text-conteudo-fraco">
                    O histórico integral traz no máximo 20 interações por atendimento. Abra o
                    atendimento para ver a timeline completa.
                  </p>
                ) : null}
              </details>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Par({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex min-w-0 gap-1.5">
      <dt className="shrink-0 font-semibold text-conteudo-suave">{rotulo}:</dt>
      <dd className="min-w-0 truncate text-conteudo">{valor}</dd>
    </div>
  )
}
